/**
 * @fsd_page AppHome - главная
 */

import s from './AppHome.module.sass'

import React, { useMemo } from 'react'

import { useAppSelector } from '@app/model/store'

import { RestaurantHomeFilters } from '@pages/AppHome/ui/RestaurantHomeFilters'
import { TagsRoll } from '@pages/AppHome/ui/TagsRoll'

import { CompilationGrid } from '@widgets/News/CompilationGrid'
import { NewsCard } from '@widgets/News/NewsCard'
import { NewsCardCover } from '@widgets/News/NewsCardCover'
import { RestaurantMainCards } from '@widgets/Restaurant/RestaurantMainCards'
import { RestCard, RestCardSkeleton } from '@widgets/Restaurant/RestCards/RestCard'
import { SelectionCardCompilation, SelectionCardCompilationSkeleton } from '@widgets/Selection/SelectionCardCompilation'

import { PromoRoll } from '@features/PromoRoll'

import { AdvertSidebarSticky } from '@shared/ui/Layout/AdvertTemplate/ui/AdvertSidebarSticky'
import { AdvertTemplate } from '@shared/ui/Layout/AdvertTemplate'
import { ContainerLong } from '@shared/ui/Layout/Containers/ContainerLong'
import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ContainerTwoColumns } from '@shared/ui/Layout/Containers/ContainerTwoColumns'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { SectionCarousel } from '@shared/ui/Layout/SectionCarousel'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useGetABTests } from '@shared/lib/hooks/useGetABTests'
import SectionTitle from '@shared/ui/Layout/SectionTitle/SectionTitle'
import useFeatureFlag from '@shared/lib/hooks/useFeatureFlag'

// создание массива скелетонов для карусели
const createSkeletons = (
  count = 4,
  sizes = 'L ML',
  SkeletonComponent: React.ComponentType<{ sizes: string }> = RestCardSkeleton,
) => {
  return Array.from(Array(count).keys()).map((i, idx) => {
    return <SkeletonComponent key={`skeleton-${idx}`} sizes={sizes} />
  })
}

export default function AppHome() {
  const { isMobile, isTablet, isDesktopLarge } = useClientResponsive()
  const currentCity = useAppSelector((state) => state.global.currentCity)
  const stateHome = useAppSelector((state) => state.pages.app_home)
  const {
    featureRests,
    tags,
    bookingRests,
    specialRests,
    ratingRests,
    afishaSelections,
    weekPlans,
    restaurantsNews,
    otherNews,
    interviews,
  } = stateHome.sections

  const tagsFormatted = useMemo(() => {
    if (!tags.list?.length) return []

    return tags.list
      .map((tag) => tag.tag_object)
      .filter((obj): obj is { id: number; name: string; slug: string; abs_url: string } => {
        return !!obj && typeof obj.id === 'number' && !!obj.name && !!obj.slug && typeof obj.abs_url === 'string'
      })
  }, [tags.list])

  const ABTests = useGetABTests()

  const ABTestBooking = ABTests.find((test) => test.name === 'AB_OPEN_BOOKING_1')

  // AB enabled for group C
  if (ABTestBooking) {
    ABTestBooking.group = 'C'
  }

  const isABTestC = ABTestBooking?.group === 'C'

  const activeMainCardsSkeleton = featureRests.loading
  const activeTagsRollSkeleton = tags.loading

  const showMainSection = Boolean(
    activeMainCardsSkeleton || activeTagsRollSkeleton || featureRests.list?.length || tags.list?.length,
  )
  const showSectionSpecialRests = Boolean(specialRests.loading || specialRests.list?.length)
  const showBookingRests = Boolean(isABTestC && !bookingRests.loading && bookingRests.list?.length)
  const showSectionNewsInteresting = Boolean(restaurantsNews.loading || restaurantsNews.list?.length)
  const showInterviews = Boolean(interviews.loading || interviews.list?.length)
  const showSectionRecommended = Boolean(ratingRests.loading || ratingRests.list?.length)
  const showAllNews = Boolean(otherNews.loading || otherNews.list?.length)
  const showAfishaSelected = Boolean(afishaSelections.loading || afishaSelections.list?.length)
  const showWeekPlans = Boolean(weekPlans.loading || !!weekPlans?.data)

  const promoIds = ['gastrogid', 'bot-accomplice', 'possibilities-restaurateurs']
  if (useFeatureFlag('kuper')) promoIds.unshift('kuper')

  const isDev = typeof window === 'object' && window.location.search.indexOf('is_dev=true') > 0

  return (
    <ErrorBoundary>
      <main>
        {isMobile && (
          <ContainerLongLeft>
            <RestaurantHomeFilters />
          </ContainerLongLeft>
        )}

        {showMainSection && (
          <div className={s['main-section']}>
            <RestaurantMainCards list={featureRests.list || []} skeleton={activeMainCardsSkeleton} />

            <ContainerLong classNameContent={s['cover-roll-content']}>
              <TagsRoll tags={tagsFormatted} skeleton={activeTagsRollSkeleton} />
            </ContainerLong>
          </div>
        )}

        <div className={s['container-main-wrapper']}>
          {/* Маркетинговые блоки */}
          <PromoRoll promoIds={promoIds} />

          {!isMobile && !isDesktopLarge && (
            <ContainerLongLeft>
              <RestaurantHomeFilters />
            </ContainerLongLeft>
          )}

          <ContainerTwoColumns>
            <ContainerTwoColumns.Main className={s['container-main']}>
              {isDesktopLarge && <RestaurantHomeFilters />}

              {showBookingRests && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Бронируйте прямо сейчас'}
                  link={`/${currentCity?.slug}/restaurants/${
                    currentCity?.slug === 'spb'
                      ? 'restorany-kotorye-nado-bronirovat-pryamo-sejchas-2025'
                      : 'restorany-kotorye-nado-bronirovat-pryamo-sejchas'
                  }/`}
                  className={s['booking-now-rests-slider-roll']}
                  skeleton={bookingRests.loading}>
                  <SectionCarousel.Items>
                    {bookingRests.list
                      ? [
                          ...bookingRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'booking'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Бронируйте прямо сейчас`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Спецпредложения, (рестораны) */}

              {showSectionSpecialRests && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Спецпредложения'}
                  link={`/${currentCity?.slug}/restaurants/restaurant_list/owner_offer/`}
                  className={s['special-rests-slider-roll']}
                  skeleton={specialRests.loading}>
                  <SectionCarousel.Items>
                    {specialRests.list
                      ? [
                          ...specialRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'special-offer'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Спецпредложения`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Самое интересное про рестораны, (новости) */}
              {showSectionNewsInteresting && (
                <SectionCarousel
                  countSlidesOnContainer={2}
                  title={'Самое интересное про рестораны'}
                  link={`/${currentCity?.slug}/restaurant-news/`}
                  skeleton={restaurantsNews.loading}>
                  <SectionCarousel.Items>
                    {restaurantsNews.list
                      ? [
                          ...restaurantsNews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={(index + 1) % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Самое интересное про рестораны`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {showInterviews && (
                <SectionCarousel
                  countSlidesOnContainer={2}
                  title={'Интервью'}
                  link={`/${currentCity?.slug}/restaurant-news/?variety=interviu`}
                  skeleton={interviews.loading}>
                  <SectionCarousel.Items>
                    {interviews.list
                      ? [
                          ...interviews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={(index + 2) % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Интервью`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Рекомендации для вас, (рестораны) */}
              {showSectionRecommended && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Рекомендации для вас'}
                  className={s['by-rating-rests-slider-roll']}
                  skeleton={ratingRests.loading}>
                  <SectionCarousel.Items>
                    {ratingRests.list
                      ? [
                          ...ratingRests.list.map((item, index) => (
                            <RestCard
                              key={item.id || index}
                              data={item}
                              sizes={'L ML'}
                              type={'list'}
                              analytic={{
                                advertCardPlaceType: null,
                                section_name: `Рекомендации`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              <AdvertTemplate type={'100%x240 Listing1'} />

              {/* Статьи, новости, обзоры (новости) */}
              {showAllNews && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={'Статьи, новости, обзоры'}
                  link={`/${currentCity?.slug}/restaurant-news/`}
                  skeleton={otherNews.loading}>
                  <SectionCarousel.Items>
                    {otherNews.list
                      ? [
                          ...otherNews.list.map((news, index) => (
                            <NewsCard
                              key={news.id || index}
                              sizes={index % 3 === 0 ? 'L2x1 M2x1 M2x1 1x1' : 'L1x1 M1x1 M1x1 1x1'}
                              data={news}
                              analytic={{
                                section_name: `Статьи, новости, обзоры`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>{createSkeletons()}</SectionCarousel.Skeletons>
                </SectionCarousel>
              )}

              {/* Заказать доставку в Купер */}
              {isDev && showSectionSpecialRests && (
                <SectionCarousel
                  countSlidesOnContainer={3}
                  title={isMobile ? 'Заказать доставку' : 'Заказать доставку в Купер'}
                  link={`/${currentCity?.slug}/restaurants/restaurant_list/delivery/`}
                  className={s['special-rests-slider-roll']}
                  kuper={{
                    description:
                      'Рекламодатель ООО «Купер», 18+\n' + 'ОГРН 1167847381130\n' + 'Юр.адрес Санкт-Петербург',
                    token: '123',
                  }}
                  skeleton={specialRests.loading}>
                  <SectionCarousel.Items>
                    {specialRests.list?.map((item, index) => (
                      <RestCard
                        key={item.id}
                        data={item}
                        sizes={'L ML'}
                        type={'list'}
                        advert={{
                          kuper: {
                            description:
                              'Рекламодатель ООО «Купер», 18+\n' + 'ОГРН 1167847381130\n' + 'Юр.адрес Санкт-Петербург',
                            token: '123',
                            restName: item.name,
                            link: 'https://kuper.ru/',
                          },
                        }}
                        analytic={{
                          advertCardPlaceType: null,
                          section_name: `Заказать доставку в Купер`,
                          section_index: index,
                        }}
                      />
                    ))}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>
                    {Array.from(Array(4).keys()).map((i, idx) => (
                      <RestCardSkeleton key={idx} sizes={'L ML'} />
                    ))}
                  </SectionCarousel.Skeletons>
                </SectionCarousel>
              )}
            </ContainerTwoColumns.Main>

            {!isMobile && !isTablet && (
              <ContainerTwoColumns.Aside>
                <AdvertTemplate type={'240x400'} container={AdvertSidebarSticky} />
                <AdvertTemplate type={'240x400_2nd'} container={AdvertSidebarSticky} />
              </ContainerTwoColumns.Aside>
            )}
          </ContainerTwoColumns>

          {/* Планы на неделю */}
          {showWeekPlans && weekPlans?.data && (
            <ContainerLongLeft>
              <NewsCardCover
                data={weekPlans.data}
                subtitles={[weekPlans.data.readingTime]}
                analytic={{
                  section_index: 0,
                  section_name: `Планы на неделю`,
                }}
                skeleton={weekPlans.loading}
              />
            </ContainerLongLeft>
          )}

          <AdvertTemplate type={'Parallax Footer'} container={ContainerLongLeft} />

          {/* Выбор Афиши */}
          {showAfishaSelected && (
            <ContainerLongLeft>
              {isMobile ? (
                <SectionCarousel
                  title={'Выбор Афиши'}
                  link={`/${currentCity?.slug}/restaurants/afisha_selected/`}
                  countSlidesOnContainer={3}
                  skeleton={afishaSelections.loading}>
                  <SectionCarousel.Items>
                    {afishaSelections.list
                      ? [
                          ...afishaSelections.list.map((item, index) => (
                            <SelectionCardCompilation
                              data={item}
                              key={item.id}
                              sizes={'M1x1'}
                              analytic={{
                                section_name: `Выбор Афиши`,
                                section_index: index,
                              }}
                            />
                          )),
                        ]
                      : []}
                  </SectionCarousel.Items>
                  <SectionCarousel.Skeletons>
                    {/* @ts-ignore */}
                    {createSkeletons(6, 'M1x1', SelectionCardCompilationSkeleton)}
                  </SectionCarousel.Skeletons>
                </SectionCarousel>
              ) : (
                <section className={s['home-selections-grid']}>
                  <SectionTitle
                    title={'Выбор Афиши'}
                    link={`/${currentCity?.slug}/restaurants/afisha_selected/`}
                    skeleton={afishaSelections.loading}
                  />
                  <CompilationGrid
                    type={'selections'}
                    counts={'6 6 4'}
                    list={afishaSelections.list || []}
                    analytic={{ section_name: `Выбор Афиши` }}
                    skeleton={afishaSelections.loading}
                  />
                </section>
              )}
            </ContainerLongLeft>
          )}

          {!isMobile && <AdvertTemplate type={'Context_full'} container={ContainerLongLeft} />}
        </div>
      </main>
    </ErrorBoundary>
  )
}
